import * as ReportActions from "src/app/store/report/actions";
import * as SurveyActions from "src/app/store/survey/actions";
import * as FromUser from "src/app/store/user/selectors";
import * as FromReport from "src/app/store/report/selectors";
import * as FromSurvey from "src/app/store/survey/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ReportDto, SurveyDefinitionDto, SurveyFieldDto } from "@api";
import { ToastController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { UserDto } from "@api";
import { Observable, Subscription } from "rxjs";
import { first, withLatestFrom } from "rxjs/operators";
import { AppRoutes, TOAST_DEFAULT_DURATION } from "src/app/constants";
import { RootState } from "src/app/store";
import { validateSurveyResponseValue } from "src/app/utils/validate-survey-response";
import { kebabCaseToMacroCase } from "src/app/utils/kebab-case-to-macro-case";

@Component({
  selector: "app-generic-report",
  templateUrl: "./generic-report.page.html",
  styleUrls: ["./generic-report.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericReportPage implements OnInit, OnDestroy {
  public slug!: string;
  public i18nKey!: string;
  public breadcrumbs = [];

  public routes: typeof AppRoutes = AppRoutes;
  public loading$: Observable<boolean> = this.store.select(FromReport.selectLoading);
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public definition$: Observable<SurveyDefinitionDto> = this.store.select(FromSurvey.selectDefinitionBySlug(this.slug));
  public month$: Observable<string> = this.store.select(FromRouter.selectMonthFromQueryParams);
  public year$: Observable<string> = this.store.select(FromRouter.selectYearFromQueryParams);
  public fields$: Observable<SurveyFieldDto[]>;
  public reports$: Observable<ReportDto[]>;

  private $subscription: Subscription;

  constructor(private store: Store<RootState>, private i18n: TranslateService, private toastController: ToastController) {}

  public ngOnInit(): void {
    this.fields$ = this.store.select(FromSurvey.selectDefinitionFieldsBySlug(this.slug));
    this.$subscription = this.month$.pipe(withLatestFrom(this.year$)).subscribe(([month, year]) => {
      const date = new Date();
      date.setMonth(+month - 1);
      date.setFullYear(+year);
      this.reports$ = this.store.select(
        FromReport.selectReportsForDate(
          kebabCaseToMacroCase(this.slug) as ReportDto.DataSourceEnum,
          date.getMonth() + 1,
          date.getFullYear(),
        ),
      );
    });
  }

  public ngOnDestroy(): void {
    this.$subscription?.unsubscribe();
  }

  public storeResponses(): void {
    let canSendForm: boolean = true;

    this.fields$.pipe(withLatestFrom(this.reports$), first()).subscribe(([fields, reports]) => {
      fields.forEach(field => {
        const value = reports.find(report => report.dataDefinitionId === field.dataDefinitionId)?.value?.toString();
        const { error, params } = validateSurveyResponseValue(field, value);
        if (error) {
          this.store.dispatch(ReportActions.storeReportError({ fieldId: field.id, error, params }));
          canSendForm = false;
        }
      });
    });

    this.user$.pipe(withLatestFrom(this.month$), withLatestFrom(this.year$), first()).subscribe(([[user, month], year]) => {
      const storeId: string = user.storeUsers[0]?.storeId;
      if (canSendForm) {
        this.store.dispatch(ReportActions.sendSurvey({ surveySlug: this.slug, month: +month, storeId, year: +year }));
      } else {
        this.showCheckSurveyErrorToast();
      }
    });
  }

  protected ionViewDidEnter(): void {
    this.store.dispatch(SurveyActions.getDefinitionBySlug({ slug: this.slug }));
    this.store.dispatch(ReportActions.getReportResponses({ slug: this.slug }));
  }

  private async showCheckSurveyErrorToast(): Promise<void> {
    const toast = await this.toastController.create({
      color: "danger",
      message: this.i18n.instant("SURVEYS.CHECK_ERRORS"),
      duration: TOAST_DEFAULT_DURATION,
    });

    await toast.present();
  }
}
